import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  DuplicateExperienceRequestPathParamType,
  DuplicateExperienceResponseBodyType,
  TabEnumFe,
} from "@intelligems/ig-types";
import { officialCategoryName } from "@intelligems/ig-utils";
import { useDialogs } from "@src/hooks/dialog";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import {
  addToExperiencesCache,
  addToExperiencesListCache,
  experienceKeys,
  invalidateModuleExperienceListQueries,
  offerKeys,
  redirectKeys,
} from "../queryKeys";
import { ApiRequest } from "../types/api";

const duplicateExperience = async (
  payload: ApiRequest<undefined, DuplicateExperienceRequestPathParamType>
): Promise<DuplicateExperienceResponseBodyType> => {
  const resp = await api.post<DuplicateExperienceResponseBodyType>(
    `/v2/experiences/${payload.pathParams.experienceId}/duplicate`,
    null
  );
  return resp.data;
};

export const useDuplicateExperience = (
  category: "experiment" | "personalization"
) => {
  const queryClient = useQueryClient();
  const { openDialog } = useDialogs();
  const navigate = useNavigate();
  return useMutation<
    DuplicateExperienceResponseBodyType,
    Error,
    ApiRequest<any, DuplicateExperienceRequestPathParamType>
  >({
    mutationFn: async (data) => duplicateExperience(data),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: redirectKeys.all });
      invalidateModuleExperienceListQueries(queryClient);

      if (category === "experiment") {
        // For experiments, invalidate the list query
        void queryClient.invalidateQueries({
          queryKey: experienceKeys.experimentList,
        });
      } else if (category === "personalization") {
        // Add to personalization list caches in-place (no refetch)
        addToExperiencesCache(
          queryClient,
          experienceKeys.detailedPersonalizationList,
          data.experience
        );
        addToExperiencesListCache(
          queryClient,
          experienceKeys.homeOverviewPersonalizationList,
          data.experience
        );

        // Add to offer-related caches if this is an offer experience
        if (data.experience.testTypes.hasTestCampaign) {
          addToExperiencesCache(
            queryClient,
            experienceKeys.offerPersonalizationList,
            data.experience
          );
          void queryClient.invalidateQueries({ queryKey: offerKeys.all });
          void queryClient.invalidateQueries({
            queryKey: offerKeys.experiencesList,
          });
        }

        // Add to checkout-related caches if this is a checkout experience
        if (data.experience.testTypes.hasTestCheckoutBlocks) {
          addToExperiencesCache(
            queryClient,
            experienceKeys.checkoutPersonalizationList,
            data.experience
          );
        }

        // Add to post-purchase-related caches if this is a post-purchase experience
        if (data.experience.testTypes.hasTestPostPurchase) {
          addToExperiencesCache(
            queryClient,
            experienceKeys.postPurchasePersonalizationList,
            data.experience
          );
        }
      }

      toast.success(`${officialCategoryName(category, true)} Duplicated`);
      if (
        category === "experiment" &&
        data.experience.testTypes.hasTestShipping
      ) {
        void navigate(
          `${EXPERIMENT_ROUTES.Experiment}/${data.experience.id}?action=edit&type=shipping&tab=${TabEnumFe.MODS}`
        );
        openDialog("duplicateShippingTest");
      } else if (
        category === "experiment" &&
        !window.location.pathname.includes("experiments")
      ) {
        void navigate(EXPERIMENT_ROUTES.Experiments);
      }

      if (
        category === "personalization" &&
        !window.location.pathname.includes("personalizations")
      ) {
        void navigate(PERSONALIZATION_ROUTES.Personalizations);
      }
    },
  });
};
